import { trackFileUploaded } from '#/analytics/trackFileUploaded';
import type { ApiFileUploadType } from '#/api/types/ApiFileUploadType';
import type { ApiS3FileId } from '#/api/types/ApiTypedId';
import { useFileUpload } from '#/hooks/useFileUpload';
import { useEmbedSearchWidgetApiClientFetchS3FileMeta } from '#/modules/embedSearchWidget/api/files/embedSearchWidgetApiFetchS3FileMeta';
import { embedSearchWidgetApiUploadFile } from '#/modules/embedSearchWidget/api/files/embedSearchWidgetApiUploadFile';
import { useEmbedSearchWidget } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';

type Args = {
  fileId: ApiS3FileId | null;
  fileType: ApiFileUploadType;
  onChange: (newFileId: ApiS3FileId | null) => void;
  onError: (error: Error) => void;
  canRemove: boolean;
  accept: string | null;
  maxSizeInMB: number | null;
  extraAnalyticsData: Record<string, unknown> | null;
};

export function useEmbedSearchWidgetS3FileUpload({
  fileId,
  fileType,
  onChange,
  onError,
  canRemove,
  accept,
  maxSizeInMB,
  extraAnalyticsData,
}: Args) {
  const embedSearchWidgetId = useEmbedSearchWidget().embedSearchWidget!.id;

  const [fileMeta] = useEmbedSearchWidgetApiClientFetchS3FileMeta(
    embedSearchWidgetId,
    fileId ?? null,
  );

  const { fileUploadStateAndActions } = useFileUpload({
    fileId,
    fileMeta: fileMeta ?? null,
    onChange,
    onError,
    canRemove,
    accept,
    maxSizeInMB,
    uploadFileToS3: async (newFile) => {
      const newFileId = await embedSearchWidgetApiUploadFile({
        embedSearchWidgetId,
        file: newFile,
        fileType,
      });

      trackFileUploaded({
        file: newFile,
        fileType,
        user: null,
        extraAnalyticsData,
      });

      return newFileId;
    },
  });

  return { fileUploadStateAndActions };
}
